import { Api } from 'src/app/core/api/api';
import { DealerApi } from 'src/app/core/api/dealer/dealer-api';
import { CheckpointsData } from 'src/app/core/api/dealer/schemas/checkpoints';
import { GameStatesData } from 'src/app/core/api/dealer/schemas/game-states';
import { Job, JobCancelledError, JobErrorHandler } from 'src/app/core/services/job/job';
import { UxGameServices } from 'src/app/core/services/ux-game/ux-game-services';
import { PAGE } from 'src/app/routes';

export interface CheckpointJobs {
  requestGameStates(dealerUrl: string, dealId: string): Promise<GameStatesData>;
  requestCheckpoints(
    dealerUrl: string,
    dealId: string,
    gameHash: string,
    moves: string[],
    indexes: number[],
    isGameComplete: boolean,
    prevCheckpointId: string | null,
  ): Promise<CheckpointsData | undefined>;
}

export class DealerCheckpointJobs implements CheckpointJobs {
  private abortController = new AbortController();
  private jobErrorHandler: JobErrorHandler<CheckpointsData | undefined>;

  constructor(private services: UxGameServices) {
    this.jobErrorHandler = new JobErrorHandler<CheckpointsData | undefined>(
      services.analyticsService,
      services.alertController,
      services.translocoService,
      this.abortController,
      services.router,
    );

    this.jobErrorHandler.showFatalError().onCancel(() => {
      window.location.reload();
    });
  }

  public async requestGameStates(dealerUrl: string, dealId: string): Promise<GameStatesData> {
    const job = this.services.jobService.create(() =>
      DealerApi.request(
        dealerUrl,
        this.services.httpService,
        this.services.analyticsService,
      ).gameStates(dealId),
    );
    job.showNoInternetRetry(Job.InfiniteRetry).showSpinner();
    job
      .addErrorHandler()
      .showFatalError()
      .onCancel(() => {
        this.services.router.navigate([PAGE.LOADING]);
      });
    return await job.run();
  }

  public async requestCheckpoints(
    dealerUrl: string,
    dealId: string,
    gameHash: string,
    moves: string[],
    indexes: number[],
    isGameComplete: boolean,
    prevCheckpointId: string | null,
  ): Promise<CheckpointsData | undefined> {
    return await this.jobErrorHandler.handle(() => {
      return this.handleCheckpointsRetry(
        () =>
          DealerApi.request(
            dealerUrl,
            this.services.httpService,
            this.services.analyticsService,
          ).checkpoints(dealId, gameHash, prevCheckpointId, moves, indexes),
        indexes.length > 0 || isGameComplete,
      );
    });
  }

  private async handleCheckpointsRetry(
    checkpointsRequest: () => Promise<CheckpointsData | undefined>,
    requireInternet: boolean,
  ): Promise<CheckpointsData | undefined> {
    try {
      return await checkpointsRequest();
    } catch (error) {
      if (!Api.isNoInternetError(error)) {
        await this.services.analyticsService.handleError(error);
        throw error;
      }

      if (!requireInternet) {
        return undefined;
      }

      return new Promise<CheckpointsData | undefined>((resolve, reject) => {
        const cancelledError = new JobCancelledError('User cancelled');
        this.services.alertController
          .create({
            header: this.services.translocoService.translate('job.no-internet'),
            message: this.services.translocoService.translate('job.no-internet-message-retry'),
            buttons: [
              {
                text: this.services.translocoService.translate('job.cancel'),
                handler: () => reject(cancelledError),
              },
              {
                text: this.services.translocoService.translate('job.retry'),
                handler: () => {
                  this.services.alertController.dismiss().then(() => {
                    this.handleCheckpointsRetry(checkpointsRequest, requireInternet)
                      .then(resolve)
                      .catch(reject);
                  });
                },
              },
            ],
            backdropDismiss: false,
          })
          .then((alert) => alert.present());
      });
    }
  }
}
